import { useEffect, useMemo, useRef, useState } from 'react';
import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';
import type { Photo, RunDetail, RunSummary } from './types';
import { config } from '../config';
import { formatDistance, formatDuration, formatPace, M_PER_KM } from './format';
import { LAYER_BASE, LAYER_HOVER, LAYER_SELECTED, LAYER_PHOTOS } from './RunMap';

interface Props {
  map: MapboxMap;
  run: RunSummary;
  detail: RunDetail;
  photos: Photo[];
  onClose: () => void;
}

const TRAIL_SOURCE = 'flyby-trail';
const TRAIL_LAYER = 'flyby-trail';
const TRAIL_GLOW_LAYER = 'flyby-trail-glow';
const RUNNER_SOURCE = 'flyby-runner';
const RUNNER_HALO_LAYER = 'flyby-runner-halo';
const RUNNER_LAYER = 'flyby-runner';

const FLYBY_ZOOM = 17.5;
const FLYBY_PITCH = 65;

const SPEEDS = [30, 60, 120] as const;
type Speed = (typeof SPEEDS)[number];

const PHOTO_TRIGGER_RADIUS_M = 35;

export function Flyby({ map, run, detail, photos, onClose }: Props) {
  const points = detail.points;
  const totalSimSeconds = points.length > 0 ? points[points.length - 1].t : 0;
  const unit = config.distanceUnit;

  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<Speed>(60);
  const [tick, setTick] = useState(0); // bump to force HUD refresh
  const [activePhoto, setActivePhoto] = useState<Photo | null>(null);

  const playingRef = useRef(playing);
  playingRef.current = playing;
  const speedRef = useRef<Speed>(speed);
  speedRef.current = speed;

  const elapsedRef = useRef(0);
  const idxRef = useRef(0);
  const lastFrameRef = useRef(0);
  const rafRef = useRef(0);
  const hudTimerRef = useRef(0);
  const seenPhotoIdsRef = useRef(new Set<string>());
  const photoTimeoutRef = useRef(0);
  const bearingRef = useRef(0);
  const introDoneRef = useRef(false);
  const introTimerRef = useRef(0);

  // Pre-compute closest point index for each photo (within trigger radius).
  const photoTriggers = useMemo(() => {
    const m = new Map<number, Photo>();
    const runPhotos = photos.filter((p) => p.runId === run.id);
    for (const ph of runPhotos) {
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const d = haversineMeters(points[i].lat, points[i].lon, ph.lat, ph.lon);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0 && bestDist <= PHOTO_TRIGGER_RADIUS_M) {
        // Keep the earliest match if multiple photos pin the same index.
        if (!m.has(bestIdx)) m.set(bestIdx, ph);
      }
    }
    return m;
  }, [photos, points, run.id]);

  // Set up map layers, hide other UI map layers, restore on cleanup.
  useEffect(() => {
    const start = points[0];
    if (!start) return;

    const saved = {
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch()
    };

    const previouslyDraggable = map.dragPan.isEnabled();
    const previouslyRotatable = map.dragRotate.isEnabled();
    map.dragPan.disable();
    map.dragRotate.disable();
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();

    const setup = () => {
      // Hide everyday run/photo layers so the flyby is uncluttered.
      const layersToHide = [LAYER_BASE, LAYER_HOVER, LAYER_SELECTED, LAYER_PHOTOS];
      for (const id of layersToHide) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
      }

      if (!map.getSource(TRAIL_SOURCE)) {
        map.addSource(TRAIL_SOURCE, {
          type: 'geojson',
          data: emptyLine()
        });
      }
      if (!map.getLayer(TRAIL_GLOW_LAYER)) {
        map.addLayer({
          id: TRAIL_GLOW_LAYER,
          type: 'line',
          source: TRAIL_SOURCE,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#F25C54',
            'line-width': 18,
            'line-opacity': 0.35,
            'line-blur': 10
          }
        });
      }
      if (!map.getLayer(TRAIL_LAYER)) {
        map.addLayer({
          id: TRAIL_LAYER,
          type: 'line',
          source: TRAIL_SOURCE,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#F25C54',
            'line-width': 6
          }
        });
      }
      if (!map.getSource(RUNNER_SOURCE)) {
        map.addSource(RUNNER_SOURCE, {
          type: 'geojson',
          data: pointFeature(start.lon, start.lat)
        });
      }
      if (!map.getLayer(RUNNER_HALO_LAYER)) {
        map.addLayer({
          id: RUNNER_HALO_LAYER,
          type: 'circle',
          source: RUNNER_SOURCE,
          paint: {
            'circle-radius': 16,
            'circle-color': '#F25C54',
            'circle-opacity': 0.25,
            'circle-blur': 0.4
          }
        });
      }
      if (!map.getLayer(RUNNER_LAYER)) {
        map.addLayer({
          id: RUNNER_LAYER,
          type: 'circle',
          source: RUNNER_SOURCE,
          paint: {
            'circle-radius': 7,
            'circle-color': '#FFFFFF',
            'circle-stroke-color': '#F25C54',
            'circle-stroke-width': 3
          }
        });
      }
    };

    if (map.isStyleLoaded()) setup();
    else map.once('idle', setup);

    // Cinematic intro: swoop down to the start.
    const lookahead = points[Math.min(20, points.length - 1)];
    const initialBearing = computeBearing(start.lat, start.lon, lookahead.lat, lookahead.lon);
    bearingRef.current = initialBearing;
    introDoneRef.current = false;
    const introDuration = 1600;
    map.flyTo({
      center: [start.lon, start.lat],
      zoom: FLYBY_ZOOM,
      pitch: FLYBY_PITCH,
      bearing: initialBearing,
      duration: introDuration,
      essential: true
    });
    window.clearTimeout(introTimerRef.current);
    introTimerRef.current = window.setTimeout(() => {
      introDoneRef.current = true;
      lastFrameRef.current = 0; // reset so the first post-intro frame doesn't gulp 1.6s of sim time
    }, introDuration + 60);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.clearInterval(hudTimerRef.current);
      window.clearTimeout(photoTimeoutRef.current);
      window.clearTimeout(introTimerRef.current);

      const teardown = () => {
        for (const id of [RUNNER_LAYER, RUNNER_HALO_LAYER, TRAIL_LAYER, TRAIL_GLOW_LAYER]) {
          if (map.getLayer(id)) map.removeLayer(id);
        }
        for (const id of [RUNNER_SOURCE, TRAIL_SOURCE]) {
          if (map.getSource(id)) map.removeSource(id);
        }
        for (const id of [LAYER_BASE, LAYER_HOVER, LAYER_SELECTED, LAYER_PHOTOS]) {
          if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'visible');
        }
      };
      if (map.isStyleLoaded()) teardown();
      else map.once('idle', teardown);

      if (previouslyDraggable) map.dragPan.enable();
      if (previouslyRotatable) map.dragRotate.enable();
      map.scrollZoom.enable();
      map.touchZoomRotate.enable();

      map.easeTo({
        center: saved.center,
        zoom: saved.zoom,
        bearing: 0,
        pitch: saved.pitch,
        duration: 900,
        essential: true
      });
    };
  }, [map, points]);

  // Animation loop — drives map state directly, refreshes HUD on a timer.
  useEffect(() => {
    if (totalSimSeconds === 0) return;

    const tickFrame = (now: number) => {
      // Wait for the cinematic intro flyTo to land before driving the camera —
      // calling setCenter mid-flyTo cancels it and we'd never reach FLYBY_ZOOM.
      if (!introDoneRef.current) {
        rafRef.current = requestAnimationFrame(tickFrame);
        return;
      }
      const dt = lastFrameRef.current ? (now - lastFrameRef.current) / 1000 : 0;
      lastFrameRef.current = now;
      if (playingRef.current) {
        elapsedRef.current = Math.min(totalSimSeconds, elapsedRef.current + dt * speedRef.current);
      }

      const t = elapsedRef.current;
      let i = idxRef.current;
      while (i + 1 < points.length && points[i + 1].t <= t) i++;
      while (i > 0 && points[i].t > t) i--;
      const cur = points[i];
      const nxt = points[Math.min(i + 1, points.length - 1)];
      const span = Math.max(0.001, nxt.t - cur.t);
      const a = Math.min(1, Math.max(0, (t - cur.t) / span));
      const lon = cur.lon + (nxt.lon - cur.lon) * a;
      const lat = cur.lat + (nxt.lat - cur.lat) * a;

      // Update trail (slice + interpolated head).
      const trailSrc = map.getSource(TRAIL_SOURCE) as GeoJSONSource | undefined;
      if (trailSrc) {
        const coords: [number, number][] = [];
        for (let j = 0; j <= i; j++) coords.push([points[j].lon, points[j].lat]);
        coords.push([lon, lat]);
        trailSrc.setData(lineFeature(coords));
      }
      const runnerSrc = map.getSource(RUNNER_SOURCE) as GeoJSONSource | undefined;
      if (runnerSrc) runnerSrc.setData(pointFeature(lon, lat));

      // Forward-looking bearing from current position to ~30 points ahead.
      const ahead = points[Math.min(i + 30, points.length - 1)];
      if (ahead && (ahead.lat !== lat || ahead.lon !== lon)) {
        const target = computeBearing(lat, lon, ahead.lat, ahead.lon);
        bearingRef.current = smoothBearing(bearingRef.current, target, 0.08);
      }

      map.setCenter([lon, lat]);
      map.setBearing(bearingRef.current);

      // Photo triggers — fire once per photo when its closest point is reached.
      for (let j = idxRef.current; j <= i; j++) {
        const ph = photoTriggers.get(j);
        if (ph && !seenPhotoIdsRef.current.has(ph.id)) {
          seenPhotoIdsRef.current.add(ph.id);
          window.clearTimeout(photoTimeoutRef.current);
          setActivePhoto(ph);
          photoTimeoutRef.current = window.setTimeout(() => {
            setActivePhoto((p) => (p?.id === ph.id ? null : p));
          }, 4500);
        }
      }
      idxRef.current = i;

      if (elapsedRef.current >= totalSimSeconds && playingRef.current) {
        setPlaying(false);
      }

      rafRef.current = requestAnimationFrame(tickFrame);
    };
    rafRef.current = requestAnimationFrame(tickFrame);

    // Cheap HUD refresh — 8x/sec is plenty.
    hudTimerRef.current = window.setInterval(() => setTick((n) => n + 1), 125);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.clearInterval(hudTimerRef.current);
      lastFrameRef.current = 0;
    };
  }, [map, points, totalSimSeconds, photoTriggers]);

  // HUD-derived stats (recomputed on tick).
  void tick;
  const i = idxRef.current;
  const cur = points[i] ?? points[0];
  const elapsedDistance = cur ? cur.d : 0;
  const elapsedTime = cur ? cur.t : 0;
  const lookbackIdx = Math.max(0, i - 30);
  const lb = points[lookbackIdx];
  const localPaceSPerKm =
    lb && i > lookbackIdx && cur.d > lb.d
      ? ((cur.t - lb.t) / (cur.d - lb.d)) * M_PER_KM
      : run.avg_pace_s_per_km;
  const elevFt = cur ? Math.round(cur.ele * 3.28084) : 0;
  const hr = cur && typeof cur.hr === 'number' ? cur.hr : null;
  const progress = totalSimSeconds > 0 ? elapsedRef.current / totalSimSeconds : 0;
  const isFinished = elapsedRef.current >= totalSimSeconds && totalSimSeconds > 0;

  return (
    <div className="absolute inset-0 z-50 pointer-events-none">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 p-3 md:p-4 flex justify-between items-start pointer-events-auto bg-gradient-to-b from-black/40 to-transparent">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-2 rounded-full bg-white/95 hover:bg-white text-ink-900 text-sm font-medium shadow-md"
        >
          ← Exit flyby
        </button>
        <div className="rounded-xl bg-black/60 backdrop-blur-md text-white px-3 py-2 md:px-4 md:py-2.5 shadow-md text-right">
          <div className="font-display text-base md:text-xl leading-tight">{run.name}</div>
          <div className="text-[11px] md:text-xs text-white/75 font-mono mt-0.5">
            {formatDistance(run.distance_m, unit)} · {formatDuration(run.duration_s)}
          </div>
        </div>
      </div>

      {/* Photo bubble — top-right on desktop, anchored above the HUD on mobile. */}
      {activePhoto && (
        <div
          key={activePhoto.id}
          className="absolute right-3 bottom-[170px] w-40 md:w-56 md:bottom-auto md:top-24 md:right-5 rounded-xl overflow-hidden bg-white shadow-2xl pointer-events-auto"
          style={{ animation: 'flyby-photo-in 320ms cubic-bezier(.2,.8,.2,1)' }}
        >
          <img
            src={activePhoto.thumbUrl}
            alt=""
            className="w-full h-28 md:h-40 object-cover block"
            loading="eager"
          />
        </div>
      )}

      {/* Bottom HUD */}
      <div className="absolute bottom-0 inset-x-0 p-3 md:p-5 pointer-events-auto bg-gradient-to-t from-black/70 via-black/30 to-transparent">
        <div className="max-w-3xl mx-auto rounded-2xl bg-black/65 backdrop-blur-md text-white px-4 py-3 md:px-6 md:py-4 shadow-xl">
          <div className="grid grid-cols-4 gap-3 md:gap-6 mb-3">
            <HudStat label="Time" value={formatDuration(elapsedTime)} />
            <HudStat label="Distance" value={formatDistance(elapsedDistance, unit)} />
            <HudStat label="Pace" value={formatPace(localPaceSPerKm, unit)} />
            <HudStat label={hr !== null ? 'HR' : 'Elev'} value={hr !== null ? `${hr} bpm` : `${elevFt} ft`} />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (isFinished) {
                  elapsedRef.current = 0;
                  idxRef.current = 0;
                  seenPhotoIdsRef.current.clear();
                  setActivePhoto(null);
                }
                setPlaying((v) => !v);
              }}
              className="w-10 h-10 rounded-full bg-accent text-white flex items-center justify-center shadow-md hover:brightness-110 transition"
              aria-label={playing ? 'Pause' : isFinished ? 'Replay' : 'Play'}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            <div
              className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden cursor-pointer"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress * 100)}
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
                elapsedRef.current = ratio * totalSimSeconds;
                idxRef.current = 0;
                seenPhotoIdsRef.current.clear();
                setActivePhoto(null);
              }}
            >
              <div
                className="h-full bg-accent rounded-full"
                style={{ width: `${(progress * 100).toFixed(2)}%` }}
              />
            </div>
            <div className="flex items-center gap-1 text-[11px]">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpeed(s)}
                  className={`px-2 py-1 rounded font-mono transition ${
                    speed === s ? 'bg-white text-ink-900' : 'bg-white/15 text-white/80 hover:bg-white/25'
                  }`}
                >
                  {s}×
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes flyby-photo-in {
          from { transform: translate3d(20px, -6px, 0) scale(.96); opacity: 0; }
          to   { transform: translate3d(0, 0, 0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function HudStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/55 font-medium">{label}</div>
      <div className="font-mono text-base md:text-xl mt-0.5 truncate">{value}</div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
      <path d="M2.5 1.5 L10 6 L2.5 10.5 Z" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
      <rect x="2.5" y="1.5" width="2.5" height="9" rx="0.6" />
      <rect x="7" y="1.5" width="2.5" height="9" rx="0.6" />
    </svg>
  );
}

function emptyLine(): GeoJSON.Feature<GeoJSON.LineString> {
  return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } };
}
function lineFeature(coords: [number, number][]): GeoJSON.Feature<GeoJSON.LineString> {
  return { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } };
}
function pointFeature(lon: number, lat: number): GeoJSON.Feature<GeoJSON.Point> {
  return { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [lon, lat] } };
}

function computeBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// Smooth bearing along the shortest arc to avoid 359° → 1° flips.
function smoothBearing(current: number, target: number, alpha: number): number {
  let diff = ((target - current + 540) % 360) - 180;
  return (current + diff * alpha + 360) % 360;
}

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const dφ = ((lat2 - lat1) * Math.PI) / 180;
  const dλ = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
